import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authFetch, type Persona } from "@/lib/api";
import { API_BASE_URL } from "@/lib/config";
import type { AvailableVariants } from "../model/types";

export function useAvailableVariants(jobId: string | undefined) {
  return useQuery({
    queryKey: ["available-variants", jobId],
    queryFn: async () => {
      const response = await authFetch(`${API_BASE_URL}/jobs/${jobId}/available-variants/`);
      if (!response.ok) {
        throw new Error("Failed to fetch available variants");
      }
      return (await response.json()) as AvailableVariants;
    },
    enabled: !!jobId,
    // A freshly added persona has no PromptReport rows yet when the mutation invalidates,
    // so a single refetch shows nothing. Poll while anything is still generating — same
    // shape as useJobs in lib/api.ts — and stop once every variant has settled.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.variants.some((v) => v.pending > 0)) {
        return 5_000;
      }
      return false;
    },
  });
}

export function useSetPersona() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      jobId,
      persona,
      roleSlug,
    }: {
      jobId: number;
      persona?: Persona;
      roleSlug?: string;
    }) => {
      // role_slug wins: it moves role, scenario and persona together server-side, so the
      // system prompt matches the reports being generated. A bare persona is the legacy
      // strip flip between already-cached variants.
      const body = roleSlug ? { role_slug: roleSlug } : { persona };
      const response = await authFetch(`${API_BASE_URL}/jobs/${jobId}/set-persona/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to switch persona");
      }
      return response.json() as Promise<{ persona: Persona; role_slug: string | null }>;
    },
    onSuccess: (_, { jobId }) => {
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
      queryClient.invalidateQueries({ queryKey: ["prompt-reports", String(jobId)] });
      // The visible tab renders via usePromptReport's ["prompt-report", jobId, reportType,
      // language] query, which doesn't change when the persona switch keeps the same tab
      // (e.g. SUMMARY, the first tab for nearly every persona) and language. Without this,
      // the old persona's cached content keeps rendering even though the backend now
      // resolves that report by the job's new persona. Partial-key invalidation (no
      // reportType/language) clears every tab for this job in one call — same pattern as
      // useRevertSnapshot's onSuccess in lib/api.ts.
      queryClient.invalidateQueries({ queryKey: ["prompt-report", String(jobId)] });
      const variantsKey = { queryKey: ["available-variants", String(jobId)] };
      queryClient.invalidateQueries(variantsKey);
      // The Celery task creates the new persona's PromptReport rows only after this
      // response has returned, so the refetch above can still land on zero pending and
      // leave useAvailableVariants' refetchInterval switched off. A short burst of delayed
      // refetches hands the countdown over to the poll even if the worker queue is briefly
      // backed up; once any refetch lands on pending > 0, refetchInterval takes over.
      for (const delayMs of [2_000, 5_000, 10_000]) {
        setTimeout(() => queryClient.invalidateQueries(variantsKey), delayMs);
      }
    },
  });
}
